import { memo, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Popover } from '@mantine/core';
import { useLazyPopover } from '../../molecules/PopoverAnchor';
import { usePortalTarget } from '../../provider/UiContext';
import { DTB_POPUP_Z } from '../popupZIndex';
import { HiOutlineFunnel, HiOutlineMagnifyingGlass, HiXMark } from 'react-icons/hi2';
import { pluralize } from '../../lib/pluralize';
import { IDataTableFacetValue } from '../DataTable.types';
import { facetLimitOf, type IDataTableFacetLimit } from '../dataTableServerSource';
import { FailureReason } from './DataTableFailure';
import { DataTableHeaderFilterList } from './DataTableHeaderFilterList';
import { type HeaderFilterPageLoader, type HeaderFilterValue, useHeaderFilterPages } from './useHeaderFilterPages';

export type { HeaderFilterPageLoader } from './useHeaderFilterPages';

/** Пауза ввода перед серверным поиском значений. */
const SEARCH_DELAY = 300;

interface IHeaderFilterProps {
    /** Поле колонки: уходит в колбэки, чтобы они были общими и стабильными. */
    columnId: string;
    header: string;
    selected?: (string | null)[];
    /** Значения со счётчиками; вычисляются лениво при открытии. Promise = серверный
     *  источник — на время загрузки попап показывает «Загрузка…». Сигнал отменяет запрос,
     *  когда попап закрыт, размонтирован или сменилась `scope`. */
    getFacet: (columnId: string, signal: AbortSignal) => IDataTableFacetValue[] | Promise<IDataTableFacetValue[]>;
    /** Значения порциями (источник с `facetPage`): вместо `getFacet` список грузится при прокрутке. */
    loadPage?: HeaderFilterPageLoader;
    /** Поиск по значениям уходит в `loadPage`; иначе ищется по загруженному. */
    serverSearch?: boolean;
    /** Выборка, в рамках которой считаются значения: смена при открытом попапе запрашивает их заново. */
    scope?: string;
    onChange: (columnId: string, values: (string | null)[] | null) => void;
}

/** Отказ загрузки значений — причина и «Повторить» (канон отказов таблицы). */
const HeaderFilterFailure = ({ error, onRetry }: { error: unknown; onRetry: () => void }) => (
    <div className="dtb-hfempty dtb-hfempty--fail" role="alert">
        <span className="dtb-hfempty__text">
            <span className="dtb-hfempty__title">Значения не загрузились</span>
            <span className="dtb-hfempty__reason">
                <FailureReason error={error} />
            </span>
        </span>
        <button type="button" className="dtb__fail-retry dtb__fail-retry--inline" onClick={onRetry}>
            Повторить
        </button>
    </div>
);

/** Пометка урезанного списка: сколько значений показано или по скольким строкам они собраны */
const limitText = ({ limit, by }: IDataTableFacetLimit) => {
    const count = limit.toLocaleString('ru-RU');
    if (by === 'rows') return `Значения — по первым ${count} ${pluralize(limit, ['строке', 'строкам', 'строкам'])}`;
    return `Показаны первые ${count} ${pluralize(limit, ['значение', 'значения', 'значений'])}`;
};

/** Header-фильтр колонки (воронка в шапке, как DX headerFilter): мультивыбор значений
 *  со счётчиками вхождений и поиском; применяется сразу по клику. */
const DataTableHeaderFilter = memo((props: IHeaderFilterProps) => {
    const { columnId, header, selected, getFacet, loadPage, serverSearch, scope, onChange } = props;
    const portalTarget = usePortalTarget();
    const [opened, setOpened] = useState(false);
    const [search, setSearch] = useState('');
    const [facet, setFacet] = useState<IDataTableFacetValue[] | null>(null);
    /** Отказ запроса значений — причина показывается в воронке (канон отказов таблицы) */
    const [failure, setFailure] = useState<{ error: unknown } | null>(null);
    /** «Повторить» после отказа: запрос значений тот же, эффект перезапускается. */
    const [attempt, setAttempt] = useState(0);
    const active = !!selected?.length;
    // Popover — с первого открытия: воронка есть в каждой колонке шапки
    const popover = useLazyPopover({ opened, haspopup: 'dialog' });

    // getFacet читаем только в момент открытия (identity колбэка нестабильна по рендерам)
    useEffect(() => {
        if (!opened) {
            setFacet(null);
            setFailure(null);
            setSearch('');
            return;
        }
        // порционный список ведёт useHeaderFilterPages
        if (loadPage) return;
        const controller = new AbortController();
        setFailure(null);
        // список прежней выборки (сменилась scope) не показываем, пока идёт новый
        setFacet(null);
        const result = getFacet(columnId, controller.signal);
        if (Array.isArray(result)) {
            setFacet(result);
        } else {
            result
                .then((values) => {
                    if (!controller.signal.aborted) setFacet(values);
                })
                .catch((error: unknown) => {
                    if (!controller.signal.aborted) setFailure({ error });
                });
        }
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, attempt, scope]);

    // серверный поиск — после паузы ввода: каждая буква не должна начинать список заново
    const searchOnServer = !!loadPage && !!serverSearch;
    const [serverQuery, setServerQuery] = useState('');
    useEffect(() => {
        const query = searchOnServer ? search.trim() : '';
        if (!query) {
            setServerQuery('');
            return;
        }
        const timer = setTimeout(() => setServerQuery(query), SEARCH_DELAY);
        return () => clearTimeout(timer);
    }, [search, searchOnServer]);
    const pages = useHeaderFilterPages({
        enabled: opened,
        columnId,
        loadPage,
        search: serverQuery,
        scope,
        attempt,
        selected,
    });

    const loading = loadPage ? pages.initialLoading : opened && facet == null && !failure;
    const listFailure = loadPage ? (pages.values.length ? null : pages.failure) : failure;
    const limit = !loadPage && facet ? facetLimitOf(facet) : undefined;
    const visible = useMemo((): HeaderFilterValue[] => {
        const q = search.trim().toLowerCase();
        const matches = (f: HeaderFilterValue) => f.label.toLowerCase().includes(q);
        if (!loadPage) {
            if (!facet) return [];
            return q ? facet.filter(matches) : facet;
        }
        if (pages.pinned == null) return [];
        // порции уже найдены сервером, если его ответ — на набранное; иначе (поиск по загруженному, пауза ввода)
        // ищем по подписи. Отмеченное сверху остаётся, если его нашёл сервер (число «1 500,5» — не подстрока
        // подписи), и тогда не повторяется ниже.
        const found = searchOnServer && serverQuery === search.trim();
        const serverKeys = new Set(pages.values.map((f) => f.key));
        const pinned = q ? pages.pinned.filter((f) => matches(f) || (found && serverKeys.has(f.key))) : pages.pinned;
        const shown = new Set(pinned.map((f) => f.key));
        const rest = pages.values.filter((f) => !shown.has(f.key));
        return [...pinned, ...(q && !found ? rest.filter(matches) : rest)];
    }, [facet, search, loadPage, searchOnServer, serverQuery, pages.pinned, pages.values]);
    /** Поиск по загруженному: следующие порции — по кнопке, иначе пустой результат выкачал бы весь список. */
    const searchLoaded = !!loadPage && !searchOnServer && !!search.trim();

    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const toggle = (key: string | null) => {
        const current = selected ?? [];
        const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
        onChange(columnId, next.length ? next : null);
    };

    // Shift-диапазонный выбор значений (как в DX-воронке): диапазон получает
    // состояние кликнутого значения. Якорь — индекс в видимом списке, смена
    // поиска, ответа сервера на него, выборки или переоткрытие делают его бессмысленным.
    const lastIndexRef = useRef<number | null>(null);
    useEffect(() => {
        lastIndexRef.current = null;
    }, [search, serverQuery, scope, opened]);
    const onValueClick = (index: number, key: string | null) => (e: MouseEvent<HTMLInputElement>) => {
        const anchor = lastIndexRef.current;
        lastIndexRef.current = index;
        if (!e.shiftKey || anchor == null || anchor === index) return;
        e.preventDefault(); // гасим нативный toggle — диапазон ставим сами
        const current = selected ?? [];
        const target = !current.includes(key);
        const [from, to] = anchor < index ? [anchor, index] : [index, anchor];
        const set = new Set(current);
        visible.slice(from, to + 1).forEach((f) => (target ? set.add(f.key) : set.delete(f.key)));
        const next = [...set];
        onChange(columnId, next.length ? next : null);
    };

    return (
        // клики по воронке/попапу не должны долетать до заголовка (там сортировка)
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {/* якорь — всегда первый узел (useLazyPopover) */}
            <button
                type="button"
                {...popover.anchorProps}
                className={clsx('dtb__hfunnel', (active || opened) && 'is-active', opened && 'is-open')}
                aria-label={`Фильтр по значениям: ${header}`}
                title="Фильтр по значениям"
                onClick={() => setOpened((v) => !v)}
            >
                <HiOutlineFunnel aria-hidden />
            </button>
            {popover.mounted && (
                <Popover
                    {...popover.popoverProps}
                    onChange={setOpened}
                    position="bottom-start"
                    offset={6}
                    zIndex={DTB_POPUP_Z}
                    portalProps={portalTarget ? { target: portalTarget } : undefined}
                    // фиксированная ширина ПРОПОМ: Mantine ставит на дропдаун inline
                    // width: max-content, и css-класс его не перебивает — попап
                    // открывался узким на «Загрузка…» и прыгал по ширине при поиске
                    width={300}
                    classNames={{ dropdown: 'dtb-menu dtb-hfilter' }}
                >
                    {popover.popoverTarget}
                    <Popover.Dropdown>
                        <div className="dtb-menu__head">
                            <div className="dtb-menu__label">{header}</div>
                            <button
                                type="button"
                                className="dtb-menu__close"
                                aria-label={`Закрыть фильтр: ${header}`}
                                onClick={() => setOpened(false)}
                            >
                                <HiXMark aria-hidden />
                            </button>
                        </div>
                        <div className="dtb-hfsearch">
                            <HiOutlineMagnifyingGlass aria-hidden />
                            <input
                                value={search}
                                placeholder="Поиск"
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label={`Поиск значений: ${header}`}
                            />
                        </div>
                        {/* «(Выделить все)» как в DX: сброс фильтра колонки = выбраны все;
                        выход из взаимной блокировки каскадных фильтров */}
                        <label className="dtb-menu__check dtb-hfall">
                            <input
                                type="checkbox"
                                checked={!active}
                                ref={(el) => {
                                    if (el) el.indeterminate = active;
                                }}
                                onChange={() => {
                                    if (active) onChange(columnId, null);
                                }}
                                aria-label={`Выделить все: ${header}`}
                            />
                            <span className="dtb-hfvalue">(Выделить все)</span>
                        </label>
                        {/* сервер отдал не все значения: найти остальные — строкой фильтров колонки */}
                        {limit && <div className="dtb-hfempty dtb-hflimit">{limitText(limit)}</div>}
                        <DataTableHeaderFilterList
                            values={visible}
                            selected={selectedSet}
                            onValueClick={onValueClick}
                            onToggle={toggle}
                            onNearEnd={loadPage && pages.hasMore && !searchLoaded ? pages.loadMore : undefined}
                            before={
                                <>
                                    {loading && <div className="dtb-hfempty">Загрузка…</div>}
                                    {listFailure && (
                                        <HeaderFilterFailure
                                            error={listFailure.error}
                                            onRetry={() => setAttempt((n) => n + 1)}
                                        />
                                    )}
                                    {!loading && !listFailure && !visible.length && !pages.hasMore && (
                                        <div className="dtb-hfempty">Нет значений</div>
                                    )}
                                </>
                            }
                            after={
                                loadPage && pages.values.length > 0 ? (
                                    pages.failure ? (
                                        <HeaderFilterFailure error={pages.failure.error} onRetry={pages.retry} />
                                    ) : pages.loadingMore ? (
                                        <div className="dtb-hfempty">Загрузка…</div>
                                    ) : (
                                        searchLoaded &&
                                        pages.hasMore && (
                                            <button
                                                type="button"
                                                className="dtb-menu__btn dtb-hfmore"
                                                onClick={pages.loadMore}
                                            >
                                                Искать дальше
                                            </button>
                                        )
                                    )
                                ) : null
                            }
                        />
                        <hr />
                        <button
                            type="button"
                            className="dtb-menu__btn dtb-menu__reset"
                            disabled={!active}
                            onClick={() => onChange(columnId, null)}
                        >
                            Сбросить
                        </button>
                    </Popover.Dropdown>
                </Popover>
            )}
        </span>
    );
});
DataTableHeaderFilter.displayName = 'DataTableHeaderFilter';

export default DataTableHeaderFilter;
